import { useEffect, MutableRefObject } from 'react';
import { Canvas } from 'fabric';

import { DrawData } from '../draw.constants';
import {
  SUB_MENU_PORTAL_ID,
  CONTENT_ELEMENT_ID,
  CURSOR_ELEMENT_ID,
  BRUSH_THICKNESS_ELEMENT_ID,
} from './draw-canvas.constants';
import Toolbar from './toolbar/toolbar';
import { removeAllObjects } from './draw.utils';
import * as Styled from './draw.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { PStyled } from 'approot/shared/styles/typography.style';
import { NegativeButton } from 'approot/shared/styles/buttons';
import type { InteractiveSubActivityFragment } from '../../../lesson-builder.graphql.generated';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import {
  DrawContextProvider,
  useDrawCanvasContext,
} from './draw-canvas.context';
import DrawCanvasInstruction from './draw-canvas-instruction';
import { useDrawFontLoader } from './use-draw-font-loader';
import CustomCursor from './draw-canvas-custom-cursor';
import { useDrawCanvas } from './use-draw-canvas';
import { LanguageCodes } from 'global/constants';

const CANVAS_CONTAINER_ID = 'canvas-container';
const FABRIC_ELEMENT_ID = 'fabric-container';

type Props = {
  id: string;
  type: string;
  imageUrl?: string | null;
  dimensionsData: {
    width: number;
    height: number;
  };
  subActivityProperties?: InteractiveSubActivityFragment | null;
  answer?: DrawData;
  questionData?: DrawData | null;
  onSave?: (content: DrawData) => void;
  language: LanguageCodes;
  canvasRef?: MutableRefObject<Canvas | null>;
};

const DrawCanvas = ({
  id,
  type,
  imageUrl,
  dimensionsData,
  subActivityProperties,
  answer,
  questionData,
  language,
  onSave,
  canvasRef,
}: Props) => {
  const { toolSet, rotateCursor } = useDrawCanvasContext();

  const {
    canvas,
    history,
    isInitialised,
    hasDrawing,
    historyCount,
    limit,
    isCursorClosed,
    increaseZoom,
    decreaseZoom,
    undo,
    redo,
  } = useDrawCanvas({
    id,
    type,
    imageUrl,
    dimensionsData,
    subActivityProperties,
    answer,
    questionData,
    isCreateMode: false,
    language,
    onSave,
  });

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  // Set the canvasRef so parent component can access canvas instance
  useEffect(() => {
    if (canvasRef) {
      canvasRef.current = canvas;
    }
  }, [canvas, canvasRef]);

  return (
    <Styled.Draw>
      {isVisible && (
        <ActivityBuilderModal
          isVisible
          title="Clear all"
          headerTitle="Clear all"
          actions={
            <NegativeButton
              onClick={() => {
                removeAllObjects(canvas);
                history?.saveCanvasState();
                closeModal();
              }}
            >
              Clear all
            </NegativeButton>
          }
          onClose={closeModal}
        >
          <PStyled>Are you sure you want to clear all canvas?</PStyled>
        </ActivityBuilderModal>
      )}
      <Styled.DrawBottomLeft>
        {hasDrawing && (
          <SecondaryButton type="button" onClick={openModal}>
            Clear All
          </SecondaryButton>
        )}
      </Styled.DrawBottomLeft>
      <Styled.DrawContent id={CONTENT_ELEMENT_ID}>
        <Styled.DrawCursorContainer id={CURSOR_ELEMENT_ID}>
          <CustomCursor
            isCursorClosed={isCursorClosed}
            tool={toolSet?.tool}
            rotateCursor={rotateCursor}
          />
        </Styled.DrawCursorContainer>
        <div id={BRUSH_THICKNESS_ELEMENT_ID} />
        <Styled.DrawInterface $isInitialised={isInitialised}>
          <DrawCanvasInstruction
            id={id}
            type={type}
            subActivityProperties={subActivityProperties}
          />
          {limit.isLimitReached ? (
            <Styled.DrawBanner $isError>
              You've reached the drawing limit! Please clear some space to keep
              drawing. Any changes beyond this point may not be saved.
            </Styled.DrawBanner>
          ) : limit.isWarningReached ? (
            <Styled.DrawBanner $isError={false}>
              You're almost at the limit of how much you can draw! Try finishing
              up or clearing some space to keep creating.
            </Styled.DrawBanner>
          ) : null}
          <Styled.DrawCanvas id={CANVAS_CONTAINER_ID}>
            <canvas id={FABRIC_ELEMENT_ID}></canvas>
          </Styled.DrawCanvas>
        </Styled.DrawInterface>
        <Styled.DrawSubMenu id={SUB_MENU_PORTAL_ID}></Styled.DrawSubMenu>

        <Toolbar
          canvas={canvas}
          canUndo={
            historyCount.position - 1 > 0 && historyCount.stackLength > 0
          }
          canRedo={historyCount.position < historyCount.stackLength}
          onUndo={undo}
          onRedo={redo}
          increaseZoom={increaseZoom}
          decreaseZoom={decreaseZoom}
        />
      </Styled.DrawContent>
    </Styled.Draw>
  );
};

const DrawCanvasWithContext = (props: Props) => {
  const isFontsLoaded = useDrawFontLoader();
  return isFontsLoaded ? (
    <DrawContextProvider>
      <DrawCanvas {...props} />
    </DrawContextProvider>
  ) : null;
};

export default DrawCanvasWithContext;
